import { LockStatus } from "@variocube/app-api";
import { isArray, naturalCompare } from "@variocube/app-common";
import { stylesheet } from "typestyle";
import { Avatar } from "./Avatar.js";
import { IconNames, MaterialIcon } from "./icon.js";
import { layout } from "./layout.js";
import { PushButton } from "./PushButton.js";
import { typography } from "./typography.js";
import { classNames, toT } from "./utils.js";
import { View, ViewContent } from "./view.js";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAsyncEffect, useCountdown, useFlag } from "./hooks/index.js";
import { Logger } from "@variocube/driver-common";
const log = new Logger("BoxAccess");
export function BoxAccess(props) {
    const { boxes: boxOrBoxArray, platform, autoConfirm = true, stopAutoConfirm = false, timeout = false, onConfirm, onBoxUnusable, openActions, onCancel, onTimeout = "confirm", allowCancelWhenOpen = false, boxOpenMessage, boxOpenTitle, confirmMessage, confirmTitle, strings, disableConfirm = false, } = props;
    const t = toT(strings);
    /** Ensure array and sort it */
    const boxes = useMemo(() => {
        return (isArray(boxOrBoxArray) ? boxOrBoxArray : [boxOrBoxArray])
            .sort((a, b) => naturalCompare(a.number, b.number));
    }, [boxOrBoxArray]);
    const { wasOpen, openBoxes, openAll, openFailed } = useBoxAccess({ platform, boxes });
    /** Tracks whether boxes have been open too long. */
    const [timedOut, setTimedOut, clearTimedOut] = useFlag(false);
    useEffect(clearTimedOut, [openBoxes]);
    useEffect(() => {
        if (timeout && wasOpen && openBoxes.length > 0) {
            const interval = 1000 * (typeof timeout == "number" ? timeout : 120);
            log.verbose("Starting timeout (ms)", interval);
            const t = setTimeout(() => {
                log.warn("Timeout reached");
                setTimedOut();
            }, interval);
            return () => clearTimeout(t);
        }
    }, [timeout, wasOpen, openBoxes]);
    /** Confirm when all boxes were closed. */
    const confirm = useMemo(() => wasOpen && openBoxes.length == 0, [wasOpen, openBoxes]);
    /** Whether we can automatically confirm */
    const canAutoConfirm = useMemo(() => (confirm && (Boolean(autoConfirm) || typeof autoConfirm == "number") && !stopAutoConfirm && !timedOut), [confirm, autoConfirm, stopAutoConfirm, timedOut]);
    /** Seconds left before auto confirming */
    const secondsLeft = useCountdown(typeof autoConfirm == "number" ? (autoConfirm > 0 ? autoConfirm : 1) : 20, canAutoConfirm);
    /** Seconds left before actually timing out */
    const timeoutSecondsLeft = useCountdown(typeof timeout == "number" ? timeout : 20, timedOut);
    /** Calls onConfirm when count down reaches zero. */
    useEffect(() => {
        if (secondsLeft <= 0) {
            onConfirm();
        }
    }, [secondsLeft, onConfirm]);
    /** Calls the corresponding handler on time-out. */
    useEffect(() => {
        if (timeoutSecondsLeft <= 0) {
            if (onTimeout == "confirm") {
                onConfirm();
            }
            else if (onTimeout == "cancel") {
                onCancel();
            }
            else {
                onTimeout();
            }
        }
    }, [timeoutSecondsLeft, onConfirm, onCancel, onTimeout]);
    /** Calls onConfirm when the confirmation screen is disabled and boxes are closed. */
    useEffect(() => {
        if (disableConfirm && confirm) {
            onConfirm();
        }
    }, [disableConfirm, confirm, onConfirm]);
    /** Auto confirm message */
    const autoConfirmMessage = useMemo(() => (canAutoConfirm && t("AutoConfirmMessage", { secondsLeft })), [canAutoConfirm, strings, secondsLeft]);
    /** Timeout message */
    const timeoutMessage = useMemo(() => (timedOut && t("TimeoutMessage", { secondsLeft: timeoutSecondsLeft })), [timedOut, strings, timeoutSecondsLeft]);
    //
    // Render
    //
    const boxItemSize = getBoxItemSize(boxes.length);
    const title = useMemo(() => {
        if (openFailed) {
            return (React.createElement("span", { className: typography.colorError }, t("OpenFailedTitle")));
        }
        if (confirm) {
            return confirmTitle;
        }
        return boxOpenTitle;
    }, [openFailed, t("OpenFailedTitle"), confirm, confirmTitle, boxOpenTitle]);
    const message = useMemo(() => {
        if (openFailed) {
            return t("OpenFailedMessage");
        }
        return confirm ? confirmMessage : boxOpenMessage;
    }, [openFailed, confirm, confirmMessage, boxOpenMessage]);
    const open = !confirm && !openFailed;
    return (React.createElement(View, null,
        React.createElement(ViewContent, { className: classNames(layout.horizontal, layout.spaceEvenly) },
            React.createElement("div", { className: classNames(layout.horizontalWrap, layout.center, layout.alignCenter, styles.boxAccessLeft) }, boxes.map(box => (React.createElement(BoxItem, { key: box.lock, number: box.number, description: box.description, open: openBoxes.indexOf(box) >= 0, size: boxItemSize })))),
            React.createElement("div", { className: classNames(layout.vertical, layout.center, styles.boxAccessRight) },
                React.createElement("div", null,
                    React.createElement("h1", { className: typography.title }, title),
                    React.createElement("p", null, message),
                    disableConfirm && (React.createElement("p", { className: typography.colorError }, t("ClosingDoorCannotBeUndone"))),
                    confirm && (React.createElement("p", { className: typography.muted }, autoConfirmMessage)),
                    timeoutMessage && (React.createElement("p", { className: typography.muted }, timeoutMessage)),
                    React.createElement("div", { className: classNames(layout.horizontal, layout.spaceBetween) },
                        onBoxUnusable && open && (React.createElement(PushButton, { onClick: onBoxUnusable },
                            React.createElement(MaterialIcon, { icon: IconNames.cancel_presentation }),
                            t("BoxUnusableButton"))),
                        (openFailed || (open && allowCancelWhenOpen)) && (React.createElement(PushButton, { onClick: onCancel },
                            React.createElement(MaterialIcon, { icon: IconNames.cancel_presentation }),
                            t("Cancel"))),
                        open && openActions,
                        openFailed && (React.createElement(PushButton, { primary: true, onClick: openAll },
                            React.createElement(MaterialIcon, { icon: IconNames.refresh }),
                            t("OpenFailedRetry"))),
                        confirm && (React.createElement(PushButton, { onClick: openAll },
                            React.createElement(MaterialIcon, { icon: IconNames.undo }),
                            t("ReopenBox"))),
                        confirm && (React.createElement(PushButton, { onClick: onConfirm, primary: true },
                            React.createElement(MaterialIcon, { icon: IconNames.check }),
                            t("Confirm")))))))));
}
export function useBoxAccess(options) {
    const { boxes, platform, autoOpen = true } = options;
    const secondary = platform.secondary;
    /** Tracks open boxes */
    const [openBoxes, setOpenBoxes] = useState([]);
    /** Whether open command was sent */
    const [openCommandSent, setOpenCommandSent, clearOpenCommandSent] = useFlag(false);
    /** Whether the open command timed out */
    const [openTimedOut, setOpenTimedOut, clearOpenTimedOut] = useFlag(false);
    /** Tracks whether open failed */
    const [openFailed, setOpenFailed, clearOpenFailed] = useFlag(false);
    /** Opens all boxes */
    const openAll = useCallback(async () => {
        try {
            for (const box of boxes) {
                await platform.openBoxLock(box.number);
            }
            setOpenCommandSent();
        }
        catch (error) {
            setOpenFailed();
        }
    }, [platform, boxes]);
    /** Clears timout and open failure when all boxes are open */
    useEffect(() => {
        if (openBoxes.length == boxes.length) {
            clearOpenCommandSent();
            clearOpenTimedOut();
            clearOpenFailed();
        }
    }, [openBoxes, boxes]);
    /** Handles the timeout check */
    const handleCheckTimeout = useCallback(() => {
        console.log("Checking if open timed out", { boxes, openBoxes });
        if (openBoxes.length < boxes.length) {
            const timedOutBoxes = new Set(boxes.map(box => box.number));
            openBoxes.forEach(box => timedOutBoxes.delete(box.number));
            log.error("Open timed out", { timedOutBoxes });
            setOpenTimedOut();
        }
    }, [openBoxes, boxes]);
    /** Schedules a timeout check after the open command was sent */
    useEffect(() => {
        if (openCommandSent) {
            const timeout = setTimeout(handleCheckTimeout, 90000);
            return () => clearTimeout(timeout);
        }
    }, [openCommandSent, handleCheckTimeout]);
    /** Sets initial status and opens all */
    useAsyncEffect(async () => {
        const initialOpenBoxes = [];
        for (const box of boxes) {
            if (await platform.getBoxLockStatus(box.number) == LockStatus.Open) {
                initialOpenBoxes.push(box);
            }
        }
        setOpenBoxes(initialOpenBoxes);
        if (autoOpen) {
            await openAll();
        }
    }, [boxes, platform, autoOpen, openAll]);
    /** Gets the box for a lock */
    const getBox = useCallback((lock) => {
        return boxes.find(box => (secondary ? box.secondaryLock : box.lock) == lock);
    }, [boxes, secondary]);
    /** Updates openBoxes according to the lock status change. */
    const handleLockStatus = useCallback((lock, status) => {
        const box = getBox(lock);
        if (box) {
            setOpenBoxes(prevOpenBoxes => {
                const openBoxes = new Set(prevOpenBoxes);
                switch (status) {
                    case LockStatus.Open:
                        openBoxes.add(box);
                        break;
                    case LockStatus.Closed:
                        openBoxes.delete(box);
                        break;
                }
                return [...openBoxes];
            });
        }
    }, [getBox]);
    /** Adds/removes the lock status listener */
    useEffect(() => {
        platform.addLockStatusListener(handleLockStatus);
        return () => platform.removeLockStatusListener(handleLockStatus);
    }, [platform, handleLockStatus]);
    /** Tracks whether boxes were open */
    const [wasOpen, setWasOpen] = useState(false);
    useEffect(() => setWasOpen(wasOpen => wasOpen || openBoxes.length > 0), [openBoxes]);
    return {
        openBoxes,
        openAll,
        wasOpen,
        openFailed: openFailed || openTimedOut,
    };
}
function getBoxItemSize(boxCount) {
    if (boxCount >= 7) {
        return 2;
    }
    else if (boxCount >= 3) {
        return 3;
    }
    else if (boxCount >= 2) {
        return 4;
    }
    else {
        return 6;
    }
}
function BoxItem({ number, description, open, size }) {
    return (React.createElement("div", { className: styles.boxItem },
        React.createElement(Avatar, { primary: open, size: size }, number),
        React.createElement("p", { className: typography.alignCenter }, description)));
}
const styles = stylesheet({
    boxAccessLeft: {
        width: "33%",
        alignContent: "center"
    },
    boxAccessRight: {
        width: "55%",
        paddingRight: "1.5em"
    },
    boxItem: {
        padding: "1em"
    }
});
//# sourceMappingURL=BoxAccess.js.map